import React, { useState, useEffect } from 'react';
import {
  Bot, Sparkles, Loader2, Check, ChevronLeft, CreditCard, Flame, Timer, Clock,
  Infinity as InfinityIcon, Waves,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { estimateCardioSessionMinutes } from './cardioTimeEstimator';

const METHOD_ICONS = { hiit: Flame, tabata: Timer, emom: Clock, amrap: InfinityIcon, intervals: Waves };
const LEVELS = [
  { value: 'debutant', label: 'Débutant' }, { value: 'intermediaire', label: 'Intermédiaire' }, { value: 'avance', label: 'Avancé' },
];

/** Générateur IA de programme Cardio — self-service, réutilise le même pool de crédits que
 * la génération musculation (services/workout_credits_service.py). */
export const CardioAIGenerator = ({ en, onBack, onValidated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();

  const [methods, setMethods] = useState([]);
  const [credits, setCredits] = useState(null);
  const [goal, setGoal] = useState('');
  const [preferredMethods, setPreferredMethods] = useState([]);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [level, setLevel] = useState('intermediaire');
  const [durationWeeks, setDurationWeeks] = useState(6);
  const [sessionMinutes, setSessionMinutes] = useState(25);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [payBusy, setPayBusy] = useState(false);

  useEffect(() => {
    authFetch('/api/tracking/cardio-pro/methods').then((r) => r.json()).then((d) => setMethods(d.methods || []));
    authFetch('/api/tracking/cardio-pro/credits').then((r) => r.json()).then(setCredits).catch(() => {});
  }, [authFetch]);

  const hasCredit = !!credits && (credits.ai_included_remaining > 0 || credits.ai_purchased > 0);
  const toggleMethod = (id) => setPreferredMethods((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  const poll = (jobId, attempt = 0) => {
    if (attempt >= 60) { setBusy(false); toast.error(L('La génération prend trop de temps. Réessayez plus tard.', 'Generation is taking too long. Try again later.')); return; }
    authFetch(`/api/tracking/cardio-pro/ai-generate/${jobId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((job) => {
        if (job.status === 'generating') { setTimeout(() => poll(jobId, attempt + 1), 3000); return; }
        setBusy(false);
        if (job.status === 'ready') setPreview({ job_id: jobId, program: job.program });
        else toast.error(job.error || L('La génération IA a échoué. Réessayez.', 'AI generation failed. Try again.'));
      })
      .catch(() => { setBusy(false); toast.error(L('Connexion perdue pendant la génération. Réessayez.', 'Connection lost during generation. Try again.')); });
  };

  const generate = () => {
    if (!goal.trim()) { toast.error(L('Décrivez votre objectif.', 'Describe your goal.')); return; }
    setBusy(true);
    const body = {
      goal: goal.trim(), preferred_methods: preferredMethods.length ? preferredMethods : null,
      days_per_week: +daysPerWeek, level, duration_weeks: +durationWeeks, session_duration_minutes: +sessionMinutes,
    };
    authFetch('/api/tracking/cardio-pro/ai-generate', { method: 'POST', body: JSON.stringify(body) })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(j))))
      .then((d) => poll(d.job_id))
      .catch((j) => {
        toast.error(j?.detail === 'no_ai_credit' ? L('Aucun crédit disponible.', 'No credit available.') : L('La génération IA a échoué. Réessayez.', 'AI generation failed. Try again.'));
        setBusy(false);
      });
  };

  const validate = () => {
    setValidating(true);
    authFetch(`/api/tracking/cardio-pro/ai-generate/${preview.job_id}/validate`, { method: 'POST' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { toast.success(L('Programme cardio validé et créé !', 'Cardio program validated and created!')); onValidated(); })
      .catch(() => toast.error(L('Échec de la validation.', 'Validation failed.')))
      .finally(() => setValidating(false));
  };

  const payForCredit = () => {
    setPayBusy(true);
    authFetch('/api/tracking/ai-workout/credits/checkout', { method: 'POST', body: JSON.stringify({ origin_url: window.location.origin }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { if (d.url) window.location.href = d.url; })
      .catch(() => { setPayBusy(false); toast.error(L('Paiement impossible.', 'Payment failed.')); });
  };

  if (preview) {
    return (
      <div className="space-y-4" data-testid="cardio-ai-preview-screen">
        <h2 className="text-lg font-black text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-400" /> {L('Aperçu avant validation', 'Preview before validation')}</h2>
        <p className="text-white font-semibold">{preview.program.name}</p>
        <p className="text-xs text-zinc-400 mb-1">{preview.program.goal}</p>
        <div className="space-y-3">
          {preview.program.sessions.map((s, i) => (
            <div key={i} className="bg-zinc-800/60 border border-white/10 rounded-xl p-3" data-testid={`cardio-ai-preview-session-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-white font-semibold text-sm">{s.session_label}</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-orange-500/15 text-orange-300 border border-orange-500/30 shrink-0" data-testid={`cardio-ai-preview-time-badge-${i}`}>
                  ~{estimateCardioSessionMinutes(s)} min
                </span>
              </div>
              <p className="text-xs text-zinc-500 mb-2">{L('Échauffement', 'Warmup')} {s.warmup_seconds}s · {L('retour au calme', 'cooldown')} {s.cooldown_seconds}s</p>
              <div className="space-y-2">
                {s.blocks.map((b, bi) => {
                  const Icon = METHOD_ICONS[b.method] || Flame;
                  const methodLabel = methods.find((m) => m.id === b.method)?.label || b.method;
                  return (
                    <div key={bi} className="flex items-start gap-2 text-xs text-zinc-400">
                      <Icon className="w-3.5 h-3.5 text-orange-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-orange-300 font-semibold">{methodLabel}</span> · {b.rounds} {L('tours', 'rounds')} · {b.work_seconds}s/{b.rest_seconds}s
                        <div className="text-zinc-500">{b.exercises.map((e) => e.name).join(', ')}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <button onClick={validate} disabled={validating}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-60"
            data-testid="cardio-ai-validate-btn">
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {L('Valider et créer', 'Validate and create')}
          </button>
          <button onClick={onBack} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors" data-testid="cardio-ai-cancel-btn">
            {L('Annuler', 'Cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cardio-ai-generator">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="cardio-ai-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <h2 className="text-xl font-black text-white flex items-center gap-2"><Bot className="w-5 h-5 text-orange-400" /> {L("Générer un programme Cardio avec l'IA", 'Generate a Cardio program with AI')}</h2>

      {credits && (
        <div className="rounded-xl bg-orange-500/10 border border-orange-500/25 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="cardio-ai-credit-banner">
          <p className="text-sm text-orange-200">
            {hasCredit
              ? L(`Crédit disponible : ${credits.ai_included_remaining} inclus${credits.ai_purchased ? ` + ${credits.ai_purchased} acheté(s)` : ''} — cette génération sera gratuite.`, `Credit available: ${credits.ai_included_remaining} included${credits.ai_purchased ? ` + ${credits.ai_purchased} purchased` : ''} — free generation.`)
              : L(`Crédits épuisés. Une nouvelle génération coûte ${credits.extra_price_chf} CHF.`, `Credits used up. A new generation costs ${credits.extra_price_chf} CHF.`)}
          </p>
          {!hasCredit && (
            <button onClick={payForCredit} disabled={payBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform disabled:opacity-50 whitespace-nowrap"
              data-testid="cardio-ai-pay-btn">
              {payBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />} {L(`Payer ${credits.extra_price_chf} CHF`, `Pay ${credits.extra_price_chf} CHF`)}
            </button>
          )}
        </div>
      )}

      {busy ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3" data-testid="cardio-ai-generating">
          <Loader2 className="w-10 h-10 text-orange-400 animate-spin" />
          <p className="text-zinc-300 text-sm font-semibold">{L("L'IA construit votre programme cardio...", 'AI is building your cardio program...')}</p>
          <p className="text-zinc-500 text-xs">{L("Cela peut prendre jusqu'à 60 secondes.", 'This can take up to 60 seconds.')}</p>
        </div>
      ) : !hasCredit ? (
        <div className="text-center py-8 text-sm text-zinc-500" data-testid="cardio-ai-no-credit-block">
          {L('Achetez un crédit ci-dessus pour lancer la génération.', 'Buy a credit above to start the generation.')}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Votre objectif', 'Your goal')}</label>
            <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={2}
              placeholder={L('Ex: Perdre du gras et améliorer mon endurance...', 'Ex: Lose fat and improve my endurance...')}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-orange-500/50 focus:outline-none resize-none"
              data-testid="cardio-ai-goal-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Méthodes préférées (optionnel)', 'Preferred methods (optional)')}</label>
            <div className="flex items-center gap-2 flex-wrap">
              {methods.map((m) => {
                const Icon = METHOD_ICONS[m.id] || Flame;
                const active = preferredMethods.includes(m.id);
                return (
                  <button key={m.id} onClick={() => toggleMethod(m.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${active ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-transparent' : 'bg-zinc-800/60 text-zinc-300 border-white/10 hover:border-orange-500/40'}`}
                    data-testid={`cardio-ai-method-chip-${m.id}`}>
                    <Icon className="w-3.5 h-3.5" /> {m.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Séances / semaine', 'Sessions / week')}</label>
              <input type="number" min={1} max={7} value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="cardio-ai-days-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Durée séance (min)', 'Session duration (min)')}</label>
              <input type="number" min={10} max={90} value={sessionMinutes} onChange={(e) => setSessionMinutes(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="cardio-ai-session-minutes-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Niveau', 'Level')}</label>
              <select value={level} onChange={(e) => setLevel(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="cardio-ai-level-select">
                {LEVELS.map((lv) => <option key={lv.value} value={lv.value}>{lv.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-400 uppercase mb-1.5 block">{L('Durée programme (sem.)', 'Program duration (wks)')}</label>
              <input type="number" min={1} max={52} value={durationWeeks} onChange={(e) => setDurationWeeks(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none" data-testid="cardio-ai-duration-input" />
            </div>
          </div>
          <button onClick={generate}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-[1.02] transition-transform"
            data-testid="cardio-ai-generate-btn">
            <Sparkles className="w-4 h-4" /> {L('Générer mon programme cardio', 'Generate my cardio program')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardioAIGenerator;
